// The Queue › Revisit (Phase 6.3 Part 2, agreed with Kristi Oct 30): books to come back to.
// Who's in it, what each row's Why says, and how the list sorts. No database calls here, so the
// rules can be tested on their own (tests/logic/revisit.test.ts).

import { fuzzyText, starsShort } from '../book/model';
import type { Precision } from '../book/types';
import type { Shelves } from '../shelves/load';
import type { ShelfBook } from '../shelves/model';
import { titleKey } from './model';

export type RevisitKind = 'try_again' | 'read_again' | 'abandoned' | 'passed_on';

export interface RevisitKindDef {
  key: RevisitKind;
  /** The status pill and the Show chip. */
  label: string;
  /** The row's shortcut into the shared sheets (Kristi, Oct 30: Abandoned's says Try again too). */
  shortcut: string;
  /** Abandoned and Passed on show greyed (decisions doc, Oct 21). */
  greyed: boolean;
}

/** In status order, the order "Status, newest first" uses. */
export const REVISIT_KINDS: RevisitKindDef[] = [
  { key: 'try_again', label: '↺ Try again', shortcut: 'Start again', greyed: false },
  { key: 'read_again', label: '↻ Read again', shortcut: 'Start a re-read', greyed: false },
  { key: 'abandoned', label: 'Abandoned', shortcut: 'Try again', greyed: true },
  { key: 'passed_on', label: 'Passed on', shortcut: 'Bring back', greyed: true },
];

export const kindDef = (k: RevisitKind) => REVISIT_KINDS.find((d) => d.key === k)!;

export interface RevisitRow extends ShelfBook {
  kind: RevisitKind;
}

/**
 * Who's in Revisit (Kristi, Oct 30):
 * - ↺ Try again books not on Up Next or Currently Reading (most sit in Future reads too: they're in
 *   both, and leave both when started or moved to Up Next);
 * - ↻ Read again books with a finished read and no other place (Finished, or no status);
 * - Not for me books, as Abandoned or Passed on.
 * Each book once.
 */
export function revisitRows(shelves: Shelves): RevisitRow[] {
  const seen = new Map<string, ShelfBook>();
  for (const list of Object.values(shelves)) for (const b of list) if (!seen.has(b.id)) seen.set(b.id, b);
  const finished = new Set(shelves.finished.map((b) => b.id));
  const out: RevisitRow[] = [];
  for (const b of seen.values()) {
    if (b.status === 'not_for_me') {
      out.push({ ...b, kind: b.notForMe?.kind === 'abandoned' ? 'abandoned' : 'passed_on' });
    } else if (b.tryAgain && b.status !== 'up_next' && b.status !== 'currently_reading') {
      out.push({ ...b, kind: 'try_again' });
    } else if (b.readAgain && (b.status === null || b.status === 'finished') && finished.has(b.id)) {
      out.push({ ...b, kind: 'read_again' });
    }
  }
  return out;
}

/** A read as Revisit needs it. */
export interface RevisitRead {
  outcome: 'open' | 'finished' | 'stopped';
  endedOn: string | null;
  endedPrecision: string | null;
  endedWords?: string | null;
  stoppedPercent: number | null;
  rating: number | null;
  chips?: string[];
  reason?: string | null;
  createdAt: string;
}

/** What the Why column draws on, read for the books in Revisit. */
export interface RevisitFacts {
  reads: RevisitRead[];
  /** A pass's reason, in your words. */
  passReason?: string | null;
}

/** Reads oldest first: by when they ended, undated ones by when they were recorded. */
function inOrder(reads: RevisitRead[]): RevisitRead[] {
  return [...reads].sort((a, b) => (a.endedOn ?? a.createdAt.slice(0, 10)).localeCompare(b.endedOn ?? b.createdAt.slice(0, 10)) || a.createdAt.localeCompare(b.createdAt));
}

const latest = (reads: RevisitRead[], outcome: 'finished' | 'stopped') => inOrder(reads).filter((r) => r.outcome === outcome).at(-1);

/** Stopped reads since the last finish. */
function triesOf(reads: RevisitRead[]): number {
  let n = 0;
  for (const r of inOrder(reads)) n = r.outcome === 'finished' ? 0 : r.outcome === 'stopped' ? n + 1 : n;
  return n;
}

const when = (r: RevisitRead, today?: string) => fuzzyText(r.endedOn, (r.endedPrecision as Precision) ?? null, r.endedWords, today);
const pct = (p: number | null) => (p === null || p === undefined ? null : `${Math.round(Number(p))}%`);
const reasonOf = (chips: string[] = [], words?: string | null) => [...new Set([...chips.filter(Boolean), ...(words?.trim() ? [words.trim()] : [])])].join(' · ') || null;

/**
 * The Why column (Kristi, Oct 30). Read again: the latest finish and its stars. Try again and
 * Abandoned: where you stopped, when, the stars so far, how many tries, and why. Passed on: when and why.
 */
export function whyText(row: Pick<RevisitRow, 'kind' | 'notForMe'>, facts: RevisitFacts | undefined, today?: string): string {
  const reads = facts?.reads ?? [];
  if (row.kind === 'read_again') {
    const f = latest(reads, 'finished');
    if (!f) return 'Finished';
    const on = when(f, today);
    return [on ? `Finished ${on}` : 'Finished, date not recorded', f.rating ? starsShort(Number(f.rating)) : 'not rated'].join(' · ');
  }
  if (row.kind === 'passed_on') {
    const on = row.notForMe?.on ? fuzzyText(row.notForMe.on, 'day', null, today) /* the day, as on the cards (Kristi, Nov 2) */ : '';
    const why = reasonOf(row.notForMe?.chips, facts?.passReason);
    return [on ? `Passed on ${on}` : 'Passed on', why ?? 'no reason given'].join(' · ');
  }
  const s = latest(reads, 'stopped');
  if (!s) return row.kind === 'abandoned' ? 'Abandoned' : 'Set aside';
  const tries = triesOf(reads);
  const at = pct(s.stoppedPercent);
  return [
    at ? `Stopped at ${at}` : 'Stopped',
    when(s, today) || null,
    s.rating ? `${starsShort(Number(s.rating))} so far` : null,
    row.kind === 'try_again' && tries > 1 ? `${tries} tries` : null,
    reasonOf(s.chips, s.reason),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** When a book came to Revisit, for "newest first": the stop, the finish, or the pass. */
export function setAsideOn(row: Pick<RevisitRow, 'kind' | 'notForMe' | 'tryAgainAt' | 'readAgainAt'>, facts: RevisitFacts | undefined): string | null {
  const reads = facts?.reads ?? [];
  if (row.kind === 'passed_on') return row.notForMe?.on ?? null;
  if (row.kind === 'read_again') return latest(reads, 'finished')?.endedOn ?? row.readAgainAt?.slice(0, 10) ?? null;
  return latest(reads, 'stopped')?.endedOn ?? (row.kind === 'abandoned' ? row.notForMe?.on : row.tryAgainAt?.slice(0, 10)) ?? null;
}

export type RevisitSortKey = 'status' | 'newest' | 'interest' | 'goodreads' | 'readtype' | 'copy' | 'title';
export interface RevisitSort {
  key: RevisitSortKey;
  reversed: boolean;
}

/** The Sort menu (Kristi, Oct 30: Status, newest first by default; ties by newest). */
export const REVISIT_SORTS: [RevisitSortKey, string][] = [
  ['status', 'Status, newest first'],
  ['newest', 'Newest set aside'],
  ['interest', 'Interest'],
  ['goodreads', 'Goodreads'],
  ['readtype', 'Read type'],
  ['copy', 'Copy'],
  ['title', 'Title'],
];

/**
 * The rows in a sort. Every sort breaks ties by newest set aside, then title (Kristi, Oct 30: a
 * fixed second sort rather than choosing two columns). Empty values go last whichever way it runs.
 */
const TYPE_ORDER = ['light', 'absorbing', 'demanding', 'study'];

export function sortRevisit<R extends RevisitRow>(rows: R[], sort: RevisitSort, facts: Map<string, RevisitFacts>, copyOf: (r: R) => string | null = () => null): R[] {
  const dir = sort.reversed ? -1 : 1;
  const on = new Map(rows.map((r) => [r.id, setAsideOn(r, facts.get(r.id)) ?? '']));
  const order = REVISIT_KINDS.map((k) => k.key);
  const newest = (a: R, b: R) => {
    const x = on.get(a.id)!;
    const y = on.get(b.id)!;
    if (x === y) return titleKey(a.title).localeCompare(titleKey(b.title));
    if (!x || !y) return x ? -1 : 1; // undated last
    return y.localeCompare(x);
  };
  const val = (r: R): number | string | null => {
    switch (sort.key) {
      case 'status':
        return order.indexOf(r.kind);
      case 'interest':
        return r.interest === null ? null : -r.interest;
      case 'goodreads':
        return r.goodreads === null ? null : -r.goodreads;
      case 'title':
        return titleKey(r.title);
      case 'readtype': {
        const i = r.readType ? TYPE_ORDER.indexOf(r.readType) : -1;
        return i >= 0 ? i : r.readTypeName ? 4 : null;
      }
      case 'copy':
        return copyOf(r)?.toLocaleLowerCase('en-US') ?? null; // A–Z; Not owned yet last
      default:
        return null;
    }
  };
  return [...rows].sort((a, b) => {
    if (sort.key === 'newest') return newest(a, b) * dir;
    const x = val(a);
    const y = val(b);
    if (x !== y) {
      if (x === null) return 1;
      if (y === null) return -1;
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      if (c) return c * dir;
    }
    return newest(a, b);
  });
}

/** The Show chips' counts. */
export function kindCounts(rows: RevisitRow[]): Record<RevisitKind, number> {
  const out = { try_again: 0, read_again: 0, abandoned: 0, passed_on: 0 };
  for (const r of rows) out[r.kind]++;
  return out;
}

/**
 * Your read of a book, in one short line (the author and series pages' Your read column, Discover
 * Part 2): "Reading · 27%" while you read it; else the latest finish ("Finished Jun 2026 · ★ 3.5")
 * or, when you last stopped, where ("Stopped at 31%"); empty when you haven't read it.
 */
export function yourReadText(status: string | null, facts: RevisitFacts | undefined, percent: number | null | undefined, today?: string): string {
  if (status === 'currently_reading') return ['Reading', pct(percent ?? null)].filter(Boolean).join(' · ');
  const reads = inOrder(facts?.reads ?? []).filter((r) => r.outcome !== 'open');
  const last = reads.at(-1);
  if (!last) return '';
  if (last.outcome === 'finished') {
    const on = when(last, today);
    return [on ? `Finished ${on}` : 'Finished', last.rating ? starsShort(Number(last.rating)) : null].filter(Boolean).join(' · ');
  }
  const at = pct(last.stoppedPercent);
  return [at ? `Stopped at ${at}` : 'Stopped', when(last, today) || null].filter(Boolean).join(' · ');
}
